import { memo, MouseEventHandler } from 'react'
import { StaticImageData } from 'next/legacy/image'
import styled from 'styled-components'
import noop from 'lodash/noop'

import { CssUnit } from '@/types'
import { colors } from '@/styles'
import { toUnit } from '@/utils/styles'
import CdnImage from '@/components/cdn-image'

import { ReactComponent as DefaultAvatar } from './default-avatar.svg'

const Wrapper = styled.div<{
  height: CssUnit
  width: CssUnit
  radius?: CssUnit
  top?: CssUnit
  bottom?: CssUnit
  left?: CssUnit
  right?: CssUnit
  onClick?: MouseEventHandler
  blurred?: boolean
}>`
  display: block;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  background-color: ${colors.lightGrey};
  height: ${props => toUnit(props.height) || 0};
  width: ${props => toUnit(props.width)};
  border-radius: ${props => props.radius || '50%'};
  margin-top: ${props => toUnit(props.top) || 0};
  margin-bottom: ${props => toUnit(props.bottom) || 0};
  margin-left: ${props => toUnit(props.left) || 0};
  margin-right: ${props => toUnit(props.right) || 0};

  ${props =>
    props.onClick &&
    `
    cursor: pointer;
  `}
  ${props =>
    props.blurred &&
    `
    cursor: default;
  `}

  @media print {
    margin-right: 80px;
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
`

const Shadow = styled.div<{ radius?: CssUnit }>`
  position: absolute;
  width: 100%;
  height: 100%;
  box-shadow: inset 0 1px 2px 0 rgba(0, 0, 0, 0.4);
  border-radius: ${props => props.radius || '50%'};
  z-index: 1;
`

interface Props {
  location?: string | StaticImageData
  height: number
  width: number
  shadow?: boolean
  radius?: CssUnit
  top?: CssUnit
  bottom?: CssUnit
  left?: CssUnit
  right?: CssUnit
  onClick?: MouseEventHandler
  blurred?: boolean
}

const Avatar: React.FC<Props> = ({
  location,
  height,
  width,
  shadow,
  radius,
  top,
  bottom,
  left,
  right,
  onClick,
  blurred = false
}) => {
  return (
    <Wrapper
      radius={radius}
      height={height}
      width={width}
      top={top}
      bottom={bottom}
      left={left}
      right={right}
      onClick={!blurred ? onClick : noop}
      blurred={blurred}
    >
      <ImageWrapper>
        {shadow && <Shadow radius={radius} />}
        {location ? (
          <CdnImage
            src={location}
            alt=""
            sizes={`${Math.max(width * 2, 96)}px`}
            heightMode="width"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        ) : (
          <DefaultAvatar height={height} width={width} />
        )}
      </ImageWrapper>
    </Wrapper>
  )
}

export default memo(Avatar)
